import {
  RawData,
  DataAdapter,
  DataPaths,
  ExpandedQueryPath,
  PluginArguments,
  ComponentInstanceDataPaths,
  DynamoDataForExtension,
} from '../types';
import {dataExtensions} from '../plugins';
import {memoize, weakMemo} from '@packages/utilities/memo';
import {Component} from '@packages/systems/wfdl';
import type {ExpressionElement} from '@packages/systems/wfdl-ast';
import {getElementAndComponentTypeByRendererNativeIdPath} from '../utils/getElementAndComponentTypeByRendererNativeIdPath';
import {NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import {getMd5Hash} from '@packages/utilities/md5-browser';
import {getInstanceKey} from '../utils/getInstanceKey';
import {Qualified} from '@packages/systems/wfdl-Qualified';

type CreateAdapter = (
  pluginArguments: PluginArguments,
  componentMap: Component.ComponentMap,
  dataPaths: DataPaths
) => DataAdapter;

export const createAdapter: CreateAdapter = memoize(
  (pluginArguments, componentMap, dataPaths) =>
    weakMemo((data) =>
      createGetDataForElement(pluginArguments, componentMap, dataPaths, data)
    )
);
function createGetDataForElement(
  pluginArguments: PluginArguments,
  componentMap: Component.ComponentMap,
  dataPaths: DataPaths,
  data: RawData
) {
  return function getDataForElement(
    elementId: string,
    rendererNativeIdPath: NativeId[],
    rootComponentType: Qualified<Component.ComponentName>,
    useExtendedData: boolean = true
  ) {
    const maybeElementAndComponentType =
      getElementAndComponentTypeByRendererNativeIdPath(
        elementId,
        rendererNativeIdPath,
        rootComponentType,
        componentMap
      );

    if (!maybeElementAndComponentType) {
      return null;
    }
    const {element, componentType} = maybeElementAndComponentType;

    const instanceKey = getInstanceKey(rendererNativeIdPath);
    const componentDataPaths = dataPaths.get(componentType);
    const componentInstanceDataPaths = componentDataPaths
      ? componentDataPaths[instanceKey]
      : null;

    if (!element || !componentInstanceDataPaths) {
      return null;
    }

    const dataExtension = dataExtensions.get(element.type);
    const getOriginalDataForElement = createElementDataGetter(
      data,
      componentInstanceDataPaths
    );

    if (useExtendedData && dataExtension) {
      const dynamoData: DynamoDataForExtension = {
        getDataForElement: getOriginalDataForElement,
        getDataPathForElement(el: ExpressionElement): ExpandedQueryPath {
          return componentInstanceDataPaths[el.id] || [];
        },
        getDataForPath: getDataFromPath(data),
        getInstanceHash() {
          return instanceKey.length > 0 ? getMd5Hash(instanceKey) : null;
        },
      };
      return dataExtension(element, dynamoData, pluginArguments);
    }

    return getOriginalDataForElement(element);
  };
}

const createElementDataGetter =
  (data: RawData, componentInstanceDataPaths: ComponentInstanceDataPaths) =>
  (element: ExpressionElement) => {
    const dataPath = componentInstanceDataPaths[element.id];
    if (!dataPath) {
      return null;
    }
    return getDataFromPath(data)(dataPath);
  };

const getDataFromPath = weakMemo((data: RawData) =>
  weakMemo((path: ExpandedQueryPath) => {
    return get(data, path);
  })
);

const get = (obj: RawData, path: ExpandedQueryPath) => {
  return path.reduce((acc, prop) => {
    return acc == null ? acc : acc[prop];
  }, obj);
};
